import React, { useState, useEffect, useCallback, useMemo,useRef} from "react";
import axios from "axios";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { handlePrint } from "../api/printUtils.js";
import PrintToOfficeModal from "../components/PrintToOfficeModal"; // <-- Import the new component
import "../Styles/InvoiceView.css";
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_API_URL;

const InvoiceView = () => {
  const { docno, finyear } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const doctype = useMemo(
    () => (queryParams.get("doctype") || "INVOICE").toUpperCase(),
    [queryParams]
  );

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(false);
  // --- NEW: State to manage the office printing button ---
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false); // <-- State to control modal
  const pdfWindowRef = useRef(null);
  const token = localStorage.getItem("token");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchInvoice = useCallback(async () => {
    if (!token) {
      navigate("/login");
      return;
    }
    try {
      const res = await axios.get(
        `${API_URL}/document/details?docno=${docno}&finyear=${finyear}&doctype=${doctype}`,
        { headers }
      );
      setInvoice(res.data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/login");
      else {
        toast.error(err?.response?.data?.detail || "Document not found");
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  }, [docno, finyear, doctype, headers, navigate, token]);

  useEffect(() => {
    fetchInvoice();
  }, [fetchInvoice]);



  const handlePrintToOffice = () => {
    setIsPrintModalOpen(true);
  };



  const handleSettlePayment = () => {
    if (!invoice || !invoice.totals) return;
    navigate(
      `/settle-payment?customer=${invoice.customer.customercode}` +
        `&docno=${invoice.docno}&finyear=${finyear}` +
        `&amount=${invoice.totals.grand_total}&docdate=${invoice.docdate}`
    );
  };

  // --- NEW: Handler for the Edit button ---
  const handleEdit = () => {
    if (doctype === "INVOICE") {
      navigate(`/billing/edit/${docno}/${finyear}`);
    } else {
      // Handles drafts like 'ESTIMATE' or 'DC'
      navigate(`/drafts/edit/${docno}/${finyear}?doctype=${doctype}`);
    }
  };

  const handleConvert = async () => {
    if (!invoice) return;
    if (!window.confirm(`Convert this ${doctype} #${invoice.docno} to an Invoice?`)) return;

    try {
      setConverting(true);
      const res = await axios.post(
        `${API_URL}/convert-to-invoice/${doctype}/${finyear}/${docno}`,
        null,
        { headers }
      );
      const { new_docno, new_finyear } = res.data || {};
      toast.success(`Successfully converted to Invoice #${new_docno}`);
      navigate(`/invoices/view/${new_docno}/${new_finyear}?doctype=INVOICE`, { replace: true });
    } catch (err) {
      console.error("Conversion failed", err);
      toast.error(err?.response?.data?.detail || "Failed to convert draft");
    } finally {
      setConverting(false);
    }
  };

  const effPercentFromLines = useMemo(() => {
    const items = invoice?.items || [];
    if (!items.length) return 0;
    const sumBasic = items.reduce((a, it) => a + Number(it.basic_value || 0), 0);
    const sumDisc  = items.reduce((a, it) => a + Number(it.discount || 0), 0);
    return sumBasic > 0 ? (sumDisc / sumBasic) * 100 : 0;
  }, [invoice?.items]);

  if (loading || !invoice) {
    return (
      <p style={{ textAlign: "center", padding: "2rem" }}>
        Loading {doctype.toLowerCase()} details...
      </p>
    );
  }

  const { customer, items = [], totals, payment_status, remarks, is_converted } = invoice;
  const isUnpaid = (payment_status || "").toUpperCase() === "UNPAID";

  const discountPercent =
    typeof invoice.discount_percent === "number" && invoice.discount_percent > 0
      ? invoice.discount_percent
      : effPercentFromLines;

  const fmtPct = (v) => (Number.isFinite(+v) ? `${(+v).toFixed(2)}%` : "");

  return (
    <div className="invoice-view-container">
      {/* --- Actions Toolbar --- */}
      <div className="view-actions">
        <div className="actions-left">
          <button className="btn btn-outline" onClick={() => navigate(-1)}>
            Back
          </button>
        </div>

        <div className="actions-right">
          {!is_converted && (
            <button className="btn btn-primary" onClick={handleEdit}>
              Edit
            </button>
          )}

          {doctype !== "INVOICE" && !is_converted && (
            <button
              className="btn btn-success"
              onClick={handleConvert}
              disabled={converting}
            >
              {converting ? "Converting..." : "Convert to Invoice"}
            </button>
          )}

          {doctype === "INVOICE" && isUnpaid && (
            <button className="btn btn-success" onClick={handleSettlePayment}>
              Settle Payment
            </button>
          )}

          <button
            className="btn btn-success"
            onClick={handlePrintToOffice}
          >
            Print to office
          </button>

          <button
            className="btn btn-primary"
            onClick={() => handlePrint(docno, finyear, doctype)}
          >
            Print / View PDF
          </button>
        </div>
      </div>

      {/* --- Main Details Card --- */}
      <div className="invoice-details-card">
        <div className="customer-info">
          <h4>Billed To:</h4>
          <p><strong>{customer?.customername || "N/A"}</strong></p>
          <p>{customer?.address1}</p>
          <p>{customer?.city}, {customer?.state} - {customer?.pincode}</p>
          <p><strong>GST:</strong> {customer?.tinno || "N/A"}</p>
        </div>
        <div className="invoice-meta">
          <p><strong>{doctype} No:</strong> {invoice.docno}</p>
          <p><strong>Date:</strong> {new Date(invoice.docdate).toLocaleDateString("en-GB")}</p>
          <p><strong>PO Number:</strong> {invoice.pono || "N/A"}</p>
          <p><strong>PO Date:</strong> {invoice.podate ? new Date(invoice.podate).toLocaleDateString("en-GB") : "N/A"}</p>
          {doctype === "INVOICE" && (
            <div className={`status-tag ${payment_status?.toLowerCase() || "unknown"}`}>
              {payment_status || "N/A"}
            </div>
          )}
        </div>
      </div>

      {/* --- Items Table --- */}
      <div className="items-table-view">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th className="item-name">Item Description</th>
              <th>HSN</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Tax%</th> 
              <th className="item-total">Amount</th>
              <th className="cost-header">Cost</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                <td className="item-name">{it.prodname}</td>
                <td>{it.hsncode}</td>
                <td className="item-qty">{it.qty} {it.uom}</td>
                <td className="item-rate">₹{(it.rate || 0).toFixed(2)}</td>
                <td className="item-tax">{it.taxper}%</td>
                <td className="item-total">₹{(it.total_value || 0).toFixed(2)}</td>
                <td className="item-cost">₹{(it.cost_price || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* --- Summary and Totals --- */}
      <div className="invoice-summary">
        <div className="remarks-view">
          <h4>Remarks</h4>
          <p>{remarks || "No remarks provided."}</p>

          {doctype === "INVOICE" && invoice.profit_details && (
            <div className="profit-view">
              <h4>Profitability</h4>
              <div className="profit-row">
                <span>Revenue (Net Value)</span>
                <span>₹{invoice.profit_details.total_revenue.toFixed(2)}</span>
              </div>
              <div className="profit-row">
                <span>Cost of Goods</span>
                <span>- ₹{invoice.profit_details.total_cost.toFixed(2)}</span>
              </div>
              <div className="profit-row total-profit">
                <span>Net Profit</span>
                <span>₹{invoice.profit_details.total_profit.toFixed(2)}</span>
              </div>
              <div className="profit-row margin">
                <span>Profit Margin</span>
                <span>{invoice.profit_details.profit_margin_percent.toFixed(2)}%</span>
              </div>
            </div>
          )}
        </div>

        {totals && (
          <div className="totals-view">
            {/* --- Conditionally render discount breakdown --- */}
            {totals.total_discount > 0 && (
              <>
                <div className="total-row">
                  <span>Gross Amount</span>
                  <span>₹{(totals.subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="total-row">
                  <span>
                    Discount{Number.isFinite(+discountPercent) ? ` (${fmtPct(discountPercent)})` : ""}
                  </span>
                  <span>- ₹{(totals.total_discount || 0).toFixed(2)}</span>
                </div>
              </>
            )}

            {/* --- Always visible rows --- */}
            <div className="total-row taxable-value">
              <span>Taxable Amount</span>
              <span>₹{(totals.total_taxable || 0).toFixed(2)}</span>
            </div>
            <div className="total-row">
              <span>CGST</span>
              <span>+ ₹{(totals.cgst || 0).toFixed(2)}</span>
            </div>
            <div className="total-row">
              <span>SGST</span>
              <span>+ ₹{(totals.sgst || 0).toFixed(2)}</span>
            </div>
            {totals.rounding_amount !== 0 && (
                <div className="total-row">
                    <span>Rounding</span>
                    <span>{(totals.rounding_amount || 0).toFixed(2)}</span>
                </div>
            )}
            <div className="total-row grand-total">
              <span>Grand Total</span>
              <span>₹{(totals.grand_total || 0).toFixed(2)}</span>
            </div>
          </div>
        )}
      </div>
       {isPrintModalOpen && (
        <PrintToOfficeModal
          isOpen={isPrintModalOpen}
          docno={docno}
          finyear={finyear}
          doctype={doctype}
          headers={headers}
          pdfWindow={pdfWindowRef.current}
          onClose={() => setIsPrintModalOpen(false)}
        />
      )}
    </div>
  );
};

export default InvoiceView;
